import { Box, SxProps, Theme } from '@mui/material';
import { ReactNode } from 'react';

/**
 * ContentWidthWrapper Component
 *
 * Constrains content width and centers it horizontally for better readability
 * and consistent layout across different viewport sizes.
 *
 * @example
 * ```tsx
 * // Wide variant for list/grid pages
 * <ContentWidthWrapper variant="wide">
 *   <ProjectDetailsScreen />
 * </ContentWidthWrapper>
 *
 * // Medium variant for content pages
 * <ContentWidthWrapper variant="medium">
 *   <ArticleContent />
 * </ContentWidthWrapper>
 *
 * // With additional styling
 * <ContentWidthWrapper variant="narrow" sx={{ py: 3 }}>
 *   <TextContent />
 * </ContentWidthWrapper>
 * ```
 */

type ContentWidthVariant = 'narrow' | 'medium' | 'wide' | 'full';

interface ContentWidthWrapperProps {
    /**
     * Width variant determining the maximum width constraint
     * - narrow: 1128px (text-focused content)
     * - medium: 1360px (standard content pages)
     * - wide: 1655px (list and grid layouts)
     * - full: no constraint (full viewport width)
     */
    variant: ContentWidthVariant;
    /** Child elements to render within the wrapper */
    children: ReactNode;
    /** Additional MUI sx props for custom styling */
    sx?: SxProps<Theme>;
}

const MAX_WIDTH_MAP: Record<ContentWidthVariant, string | undefined> = {
    narrow: '1128px',
    medium: '1360px',
    wide: '1655px',
    full: undefined
};

export const ContentWidthWrapper = ({ variant, children, sx }: ContentWidthWrapperProps) => {
    const maxWidth = MAX_WIDTH_MAP[variant];

    return (
        <Box
            sx={{
                width: '100%',
                maxWidth,
                marginLeft: 'auto',
                marginRight: 'auto',
                ...sx
            }}
        >
            {children}
        </Box>
    );
};
